import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../utils/cn';

const defaultClasses =
  'bg-violet-900/50 border border-violet-500 text-slate-100 text-base';

const messageVariants = cva('relative w-full rounded border p-4', {
  variants: {
    variant: {
      default: defaultClasses,
      'default-light':
        'bg-violet-900/30 border border-violet-500 text-background-color text-base',
      'default-glow': `${defaultClasses} border-2 shadow-[0_0_22px_0px_#ffacffb5]`,
      negative: 'bg-red-600/40 border border-red-500 text-base',
      positive: 'bg-green-600/40 border border-green-500 text-base',
      warning: 'bg-orange-600/[0.25] border border-orange-500 text-base',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const Message = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof messageVariants>
>(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(messageVariants({ variant }), className)}
    {...props}
  />
));
Message.displayName = 'Message';

const MessageTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn('font-medium leading-normal', className)}
    {...props}
  />
));
MessageTitle.displayName = 'MessageTitle';

const MessageDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('text-sm leading-normal', className)}
    {...props}
  />
));
MessageDescription.displayName = 'MessageDescription';

const MessageWithIcon = React.forwardRef<
  HTMLDivElement,
  Omit<React.HTMLAttributes<HTMLDivElement>, 'children' | 'title'> &
    VariantProps<typeof messageVariants> & {
      icon: React.ReactNode;
      title?: React.ReactNode;
      description?: React.ReactNode;
    }
>(({ className, variant, icon, title, description, ...props }, ref) => (
  <Message
    ref={ref}
    className={cn('flex gap-2', className)}
    variant={variant}
    {...props}
  >
    <div className={cn('flex', title ? 'pt-[0.15rem]' : 'pt-[0.1rem]')}>
      {icon}
    </div>
    <div>
      {title && <MessageTitle>{title}</MessageTitle>}
      {description && <MessageDescription>{description}</MessageDescription>}
    </div>
  </Message>
));
MessageWithIcon.displayName = 'MessageWithIcon';

export { Message, MessageTitle, MessageWithIcon, MessageDescription };
